import { useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { draftIdIn } from 'components/create-dialog/useCreateDraft';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import Loader from 'components/Loader';
import { useMemo, useState } from 'react';
import { useGetJobPositionByIdQuery } from 'store/api/jobPositions';
import { EntityType } from 'types/entity';
import { JobPositionDto, JobPositionForm } from 'types/jobPositions';
import { useClassifiers } from 'utils/useClassifiers';
import { jobPositionDtoToForm } from './jobPositionFormMapping';
import JobPositionPageForm from './JobPositionPageForm';

const USED_CLASSIFIERS = ['JOB_POSITION_TYPE', 'LANGUAGE'];

/** The add form loaded with what a draft holds, read as the job position's edit page reads it. */
const JobPositionDraftAdd = ({ draftId }: { draftId: number }) => {
    const [draft, setDraft] = useState<{ initialData: JobPositionForm; jobPosition: JobPositionDto }>();
    const { data: jobPosition } = useGetJobPositionByIdQuery(String(draftId), { refetchOnMountOrArgChange: true });
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: USED_CLASSIFIERS });

    // Frozen once loaded: a refetch must not reset what the user is typing.
    if (!draft && jobPosition && !isLoadingClassifiers) {
        setDraft({ initialData: jobPositionDtoToForm(jobPosition, classifiers), jobPosition });
    }

    return draft ? (
        <JobPositionPageForm
            type="add"
            initialData={draft.initialData}
            classifiers={classifiers}
            entityCustomFields={draft.jobPosition.customFields}
        />
    ) : (
        <Loader />
    );
};

const JobPositionBlankAdd = () => {
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: USED_CLASSIFIERS,
    });

    const { initialCustomFieldsObject, isLoading: isLoadingCustomFieldData } = useCustomFields({
        entityType: EntityType.JOB_POSITION,
    });

    // Set initial form data (derived from classifiers + custom-field defaults; render-time compute).
    const initialData = useMemo<JobPositionForm | undefined>(() => {
        if (isLoadingCustomFieldData) {
            return undefined;
        }
        const supportedLanguages = Array.from(
            new Set(
                classifiers['LANGUAGE']?.values
                    ?.map((value) => value.code)
                    .sort((a: string, b: string) => a.localeCompare(b)) ?? []
            )
        );

        return {
            name: '',
            externalId: '',
            positionTypeCode: null,
            entityTexts: supportedLanguages.map((langCode) => ({
                id: null,
                langCode,
                value: '',
                description: '',
                textMale: '',
                textFemale: '',
            })),
            ...initialCustomFieldsObject,
        };
    }, [classifiers, initialCustomFieldsObject, isLoadingCustomFieldData]);

    if (isLoadingClassifiers || isLoadingCustomFieldData || initialData === undefined) {
        return <Loader />;
    }

    return (
        <JobPositionPageForm type="add" initialData={initialData ? initialData : undefined} classifiers={classifiers} />
    );
};

/** The job position create; its URL with `?draft=<id>` edits that draft instead of a blank job position. */
const JobPositionPageAdd = () => {
    const draftId = draftIdIn(useCreateRoute().location.search);
    return draftId === null ? <JobPositionBlankAdd /> : <JobPositionDraftAdd key={draftId} draftId={draftId} />;
};

export default JobPositionPageAdd;
